import { apiFetch } from '../apiFetch'

export interface FreeStockItem {
  id: string
  po_id: string
  po_free_line_id: string
  name: string
  uom: string
  details: Array<{ label: string; value: any }> | Record<string, any>
  unit_price: number | null
  qty_on_hand: number
  qty_received_total: number
  created_at: string
  po_code?: string
}

export interface FreeStockMovement {
  id: string
  at: string
  type:
    | 'RECEIPT'
    | 'ISSUE'
    | 'CONVERT_OUT'
    | 'RECEIPT_CORRECTION'
    | 'ISSUE_CORRECTION'
    | 'CONVERT_CORRECTION'
  doc_code: string
  doc_id: string
  item_id: string
  qty_delta: number
  by: string
  note: string | null
}

export interface FreeStockDetail extends FreeStockItem {
  movements: FreeStockMovement[]
}

export function formatFreeStockDetails(raw: unknown): string {
  if (!raw) return '—'
  if (Array.isArray(raw)) {
    const parts = raw
      .filter((x) => x && x.value !== null && x.value !== undefined && x.value !== '')
      .map((x) => (x.label ? `${x.label}: ${x.value}` : String(x.value)))
    return parts.length > 0 ? parts.join(', ') : '—'
  }
  if (typeof raw === 'object') {
    const parts = Object.entries(raw as Record<string, any>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${v}`)
    return parts.length > 0 ? parts.join(', ') : '—'
  }
  return String(raw)
}

export async function getFreeStockList(params?: {
  q?: string
  po_id?: string
  limit?: number
  offset?: number
}): Promise<FreeStockItem[]> {
  const query = new URLSearchParams()
  if (params?.q?.trim()) query.set('q', params.q.trim())
  if (params?.po_id?.trim()) query.set('po_id', params.po_id.trim())
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.offset) query.set('offset', String(params.offset))

  const qs = query.toString()
  const res = await apiFetch(`/api/free-stock${qs ? `?${qs}` : ''}`)
  const body = await res.json()
  if (!res.ok || !body.ok) {
    throw new Error(body?.error?.message || 'Không thể tải danh sách hàng tự do')
  }
  return (body.data || []).map((row: any) => ({
    ...row,
    qty_on_hand: Number(row.qty_on_hand ?? 0),
    qty_received_total: Number(row.qty_received_total ?? 0),
    unit_price: row.unit_price != null ? Number(row.unit_price) : null,
  }))
}

export async function getFreeStockItem(id: string): Promise<FreeStockDetail> {
  const res = await apiFetch(`/api/free-stock/${id}`)
  const body = await res.json()
  if (!res.ok || !body.ok) {
    throw new Error(body?.error?.message || 'Không thể tải chi tiết hàng tự do')
  }
  const item = body.data
  return {
    ...item,
    qty_on_hand: Number(item.qty_on_hand ?? 0),
    qty_received_total: Number(item.qty_received_total ?? 0),
    unit_price: item.unit_price != null ? Number(item.unit_price) : null,
    movements: (item.movements || []).map((m: any) => ({
      ...m,
      qty_delta: Number(m.qty_delta ?? 0),
    })),
  }
}

/** Xóa (ẩn) hàng tự do tồn bằng 0. */
export async function deleteFreeStockItem(id: string): Promise<void> {
  const res = await apiFetch(`/api/free-stock/${encodeURIComponent(id)}`, { method: 'DELETE' })
  const body = await res.json().catch(() => ({}))
  if (!res.ok || !body.ok) throw new Error(body?.error?.message || 'Không thể xóa hàng tự do')
}
